import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, 
    Text, Flex, Box, IconButton, useBreakpointValue, 
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { runInAction, reaction } from 'mobx';
import { useLocalObservable } from 'mobx-react-lite';

// Import stores
import { servicebookingFlowStore, BookingStep } from '../store/ServiceBookingFlowStore';
import { doctorsStore } from '../store/DoctorsStore';
// Import GraphQL queries
import { useAppointmentSlotsQuery } from '../graphql/types-and-hooks';

// Import custom hooks
import { useBookingFlow } from '../hooks/useBookingFlow';
import { useDateManagement } from '../hooks/useDateManagement';
import { useSelectionHandlers } from '../hooks/useSelectionHandlers';
import { useUserDataNew } from '../hooks/useUserDataNew';
import { useStepRenderer } from '../hooks/useStepRenderer';

// Import components
import BookingSidebar from './BookingSideBar';
// import MobileFooter from './MobileFooter';
import MobileProgressBar from './MobileProgressBar';
import CreateFamilyMemberModal from './CreateFamilyMember';

// TypeScript interfaces
interface ServiceBookingFlowModalProps {
    isOpen: boolean;
    onClose: () => void;
}

/**
 * ServiceBookingFlowModal Component
 * 
 * A modal for booking appointments with a multi-step flow.
 * This implementation handles pre-selected service type from URL and doctor from AppPage.
 */
const ServiceBookingFlowModal: React.FC<ServiceBookingFlowModalProps> = observer(({ isOpen, onClose }) => {
    // Use the custom hooks first so variables are available throughout component
    const { user, isLoggedIn } = useUserDataNew();

    // Create local observable state to track store values
    const localStore = useLocalObservable(() => ({
        appointmentData: { ...servicebookingFlowStore.appointmentData },
        isServiceBooking: servicebookingFlowStore.isServiceBooking,
        configurationDetails: { ...servicebookingFlowStore.configurationDetails },
        isModalOpen: servicebookingFlowStore.isModalOpen,
        
        // Update method to sync with the main store
        updateFromStore() {
            this.appointmentData = { ...servicebookingFlowStore.appointmentData };
            this.isServiceBooking = servicebookingFlowStore.isServiceBooking;
            this.configurationDetails = { ...servicebookingFlowStore.configurationDetails };
            this.isModalOpen = servicebookingFlowStore.isModalOpen;
        }
    }));

    // Set up reaction to update local store when main store changes
    useEffect(() => {
        const storeDisposer = reaction(
            () => ({
                appointmentData: servicebookingFlowStore.appointmentData,
                isServiceBooking: servicebookingFlowStore.isServiceBooking,
                configurationDetails: servicebookingFlowStore.configurationDetails,
                isModalOpen: servicebookingFlowStore.isModalOpen
            }),
            () => {
                runInAction(() => {
                    localStore.updateFromStore();
                });
            }
        );

        return () => {
            // Clean up reaction when component unmounts
            storeDisposer();
        };
    }, [localStore]);

    // Determine if we should show the Existing Patient step based on config
    const showExistingPatientStep = React.useMemo(() => {
        return localStore.configurationDetails?.show_existing_patient_step !== false;
    }, [localStore.configurationDetails?.show_existing_patient_step]);

    // Define steps array - dynamically include/exclude Existing Patient step
    const STEPS = React.useMemo(() => {
        const baseSteps = [
            'User Selection',         // Step 1
        ];
        
        // Only include Existing Patient step if flag is true or not provided
        if (showExistingPatientStep) {
            baseSteps.push('Existing Patient');  // Step 2
        }
        
        baseSteps.push(
            'Time Selection',
            'Login',
            'Address & Medicare', 
            'Family Member',
            'Confirmation'
          );
        
        return baseSteps;
    }, [showExistingPatientStep]);

    // Responsive values
    const isBaseView = useBreakpointValue({ base: true, lg: false });
    const numberOfColumns = useBreakpointValue({ base: 2, md: 3, lg: 7 });
    
    //console.log("Number of Columns", numberOfColumns);
    // State for create family member modal
    const [isCreateFamilyMemberModalOpen, setIsCreateFamilyMemberModalOpen] = React.useState(false);

    const {
        // eslint-disable-next-line
        activeStep,
        currentStep,
        appointmentDetails,
        patient,
        selectedDoctor,
        selectedTime,
        handleCloseModal,
        // eslint-disable-next-line
        handleBackClick,
        goToLogin
    } = useBookingFlow(isOpen, onClose);

    // Add a local state for tracking booking confirmation status
    // eslint-disable-next-line
    const [isBookingConfirmed, setIsBookingConfirmed] = React.useState(false);

    // if Logged In Proceed to Confirmation
    useEffect(() => {
        if (currentStep === BookingStep.LOGIN && isLoggedIn) {
            runInAction(() => {
                // If we have a selected doctor, set it in the store
                servicebookingFlowStore.goToStep(BookingStep.ADDRESS_MEDICARE);
                servicebookingFlowStore.setActiveStep(2);

            });
        }
    }, [isLoggedIn, currentStep]);

    // Update the isBookingConfirmed state when needed based on step
    useEffect(() => {
        // Check if we're at confirmation step - this is just one approach
        setIsBookingConfirmed(currentStep === BookingStep.CONFIRMATION);
    }, [currentStep, appointmentDetails]);

    const { startDate, setStartDate } = useDateManagement(servicebookingFlowStore.appointmentData?.selectedDoctor);

    const {
        handleSelectFamilyMember,
        selectMe
    } = useSelectionHandlers();


    // Handle family member modal
    const handleOpenFamilyMemberModal = () => {
        setIsCreateFamilyMemberModalOpen(true);
    };

    const handleCloseFamilyMemberModal = () => {
        setIsCreateFamilyMemberModalOpen(false);
    };

    const [appointmentSlotsLoading, setAppointmentSlotsLoading] = React.useState(false);
    //console.log("startDate", startDate);
    // eslint-disable-next-line
    const appointmentStartDate = doctorsStore.selectedAptTypeFirstAvailableTimes?.[selectedDoctor?.doctor_id]?.date;
    // console.log("appointmentStartDate" , appointmentStartDate);
    // // convert to DateTime
    // console.log("first_slot Date", servicebookingFlowStore?.appointmentData?.selectedDoctor?.first_slot?.date);
    
    // console.log("startDate", startDate);
    const { data: appointmentSlotsData } = useAppointmentSlotsQuery({
        variables: {
            input: {
                clinic_group_crm: selectedDoctor?.clinic_group_crm,
                user_fk: selectedDoctor?.user_fk,
                location_id: selectedDoctor?.location_id,
                doctor_id: selectedDoctor?.doctor_id,
                appointment_type_id: selectedDoctor?.matched_apt_type_id?.toString(),
                start_date: startDate,
                end_date: startDate.plus({ days: numberOfColumns - 1 }),
            },
        },
        skip: !selectedDoctor || !startDate,
    });

    // Track loading manually
    useEffect(() => {
        if (!appointmentSlotsData) {
            setAppointmentSlotsLoading(true);
        } else {
            setAppointmentSlotsLoading(false);
        }
    }, [appointmentSlotsData]);

    // Get the step renderer function
    const renderStep = useStepRenderer({
        currentStep,
        isForUser: localStore.appointmentData?.isForUser ?? true,
        startDate,
        setStartDate,
        appointmentSlotsData,
        appointmentSlotsLoading,
        numberOfColumns,
        selectedTime: selectedTime || null,
        handleCloseModal,
        selectMe,
        handleSelectFamilyMember,
        handleOpenCreateFamilyMemberModal: handleOpenFamilyMemberModal,
        showExistingPatientStep // Pass this to the step renderer
    });
    
    // IMPORTANT FIX: Use React.useMemo to create computed values from observables
    // This ensures they're accessed within a reactive context
    const modalOpen = React.useMemo(() => localStore.isModalOpen, [localStore.isModalOpen]);
    const appointmentTypeName = React.useMemo(() => 
        localStore.appointmentData?.appointmentType?.display_name, 
        [localStore.appointmentData?.appointmentType?.display_name]
    );
   

    return (
        <>
            <Modal isOpen={modalOpen} onClose={handleCloseModal} size="full">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader display="flex" justifyContent="space-between" alignItems="center">
                        <Text fontSize="lg" fontWeight="bold">
                            Booking Appointment for {appointmentTypeName || "..."}
                        </Text>
                        <IconButton icon={<CloseIcon />} aria-label="Close modal" onClick={handleCloseModal} variant="ghost" />
                    </ModalHeader>
                    <ModalBody>
                        <Flex>
                            {/* Sidebar */}
                            <BookingSidebar
                                activeStep={servicebookingFlowStore.activeStep}
                                user={user}
                                isLoggedIn={isLoggedIn}
                                patient={patient || null}
                                selectedDoctor={selectedDoctor || null}
                                selectedTime={selectedTime || null}
                                goToLogin={goToLogin}
                                showExistingPatientStep={showExistingPatientStep}
                            />

                            {/* Main Content */}
                            <Box flex="1" display="flex" flexDirection="column" justifyContent="center" alignItems="center" width="100%">
                                {/* Progress Bar for Mobile */}
                                {isBaseView && (
                                    <MobileProgressBar
                                        currentStep={currentStep}
                                        totalSteps={STEPS.length}
                                        showExistingPatientStep={showExistingPatientStep}
                                    />
                                )}
                                {/* Current Step Content */}
                                <Box flex="1" px={6} display="flex" flexDirection="column" justifyContent="center" alignItems="center" width="100%">
                                    {renderStep()}
                                </Box>
                            </Box>
                        </Flex>
                    </ModalBody>

                    {/* Footer with Back Button (Desktop) */}
                     {/* Back Button 
                    <ModalFooter
                        justifyContent="space-between"
                        bg="white"
                        boxShadow="md"
                        p={5}
                        position="sticky"
                        bottom="0"
                        width="100%"
                        display={{ base: 'none', lg: 'flex' }}
                    >
                        {!(currentStep === BookingStep.CONFIRMATION && isBookingConfirmed) ? (
                           
                            <Button
                                onClick={handleCustomBackClick}
                                variant="outline"
                                borderColor={isServiceBooking ? buttonBgColor : "#B92F2F"}
                                color={isServiceBooking ? buttonBgColor : "#B92F2F"}
                                border="2px"
                                borderRadius={30}
                                minWidth={'150px'}
                                width={'15%'}
                                _hover={{
                                    bg: isServiceBooking ? buttonBgColor : "#B92F2F",
                                    color: "#fff",
                                }}
                            >
                                Back
                            </Button>
                          
                        ) : null}
                        <Box flexGrow={1} />
                    </ModalFooter>
                    */}
                    {/* Mobile Footer */}
                    {/* {isBaseView && (
                        <MobileFooter
                            currentStep={currentStep}
                            isLoggedIn={isLoggedIn}
                            user={user}
                            goToLogin={goToLogin}
                            handleBackClick={handleBackClick}
                            isConfirmed={isBookingConfirmed}
                            showExistingPatientStep={showExistingPatientStep}
                        />
                    )} */}
                </ModalContent>
            </Modal>

            {/* Family Member Creation Modal */}
            <CreateFamilyMemberModal
                isOpen={isCreateFamilyMemberModalOpen}
                onClose={handleCloseFamilyMemberModal}
            />
        </>
    );
});

export default ServiceBookingFlowModal;